import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import API_BASE_URL from '../config/baseUrl';
import { USER_LEVELS, LEVEL_NAMES } from '../config/menuItems';

const CompletedFancyBetsPage = () => {
  const { matchId, fancyId, matchName, fancyName } = useParams();
  const navigate = useNavigate();

  // Get user info and access token from Redux
  const { user, accessToken } = useSelector((state) => state.auth);
  const loggedInUserLevel = user?.userTypeInfo || USER_LEVELS.CLIENT;
  const loggedInUserId = user?.userId || '';

  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Downline levels state - array of levels, each with userId, userLevel, data, and loading state
  const [downlineLevels, setDownlineLevels] = useState([]);
  const [loadingLevels, setLoadingLevels] = useState({});

  useEffect(function () {
    const fetchBets = async function () {
      setLoading(true);
      setError(null);

      try {
        if (!accessToken) {
          setError('No access token found');
          setLoading(false);
          return;
        }

        const response = await axios.post(
          API_BASE_URL + '/report/get-session-bets',
          {
            matchId: matchId,
            userId: '',
            marketId: fancyId,
            matchCompleted: true,
          },
          { headers: { Authorization: 'Bearer ' + accessToken } }
        );

        const data = response.data;

        if (data.status === true && data.data) {
          setBets(data.data);
        } else {
          setBets([]);
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch bets');
        setBets([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBets();
  }, [matchId, fancyId, accessToken]);

  // ── fetch initial downline level on mount ──────────────────────────────
  useEffect(function () {
    // Only fetch if user is not a client (client has no downline)
    if (loggedInUserLevel > USER_LEVELS.CLIENT && loggedInUserId && accessToken) {
      fetchDownlineLevel(loggedInUserId, loggedInUserLevel, 0);
    }
  }, [loggedInUserId, loggedInUserLevel, matchId, fancyId, accessToken]);

  // ── fetch downline for a specific level ───────────────────────────────
  const fetchDownlineLevel = async function (userId, userLevel, levelIndex) {
    const levelKey = `${levelIndex}-${userId}`;
    
    setLoadingLevels((prev) => ({ ...prev, [levelKey]: true }));

    try {
      if (!accessToken) {
        setLoadingLevels((prev) => ({ ...prev, [levelKey]: false }));
        return;
      }

      const response = await axios.post(
        API_BASE_URL + '/urbbet/market-pnl-levelwise',
        {
          marketId: fancyId,
          matchId: matchId,
          userId: userId,
        },
        { headers: { Authorization: 'Bearer ' + accessToken } }
      );

      const data = response.data;

      if (data.status === true && data.data && data.data.length > 0) {
        // Add or update this level
        setDownlineLevels((prevLevels) => {
          const newLevels = [...prevLevels];
          
          // Remove all levels after this one (when clicking a different user)
          const existingIndex = newLevels.findIndex(
            (level) => level.levelIndex === levelIndex
          );
          
          if (existingIndex !== -1) {
            newLevels.splice(existingIndex);
          }

          // Add the new level
          newLevels.push({
            levelIndex: levelIndex,
            userId: userId,
            userLevel: userLevel,
            data: data.data,
          });

          return newLevels;
        });
      } else {
        // No data - still update to show empty state
        setDownlineLevels((prevLevels) => {
          const newLevels = [...prevLevels];
          const existingIndex = newLevels.findIndex(
            (level) => level.levelIndex === levelIndex
          );
          
          if (existingIndex !== -1) {
            newLevels.splice(existingIndex);
          }

          newLevels.push({
            levelIndex: levelIndex,
            userId: userId,
            userLevel: userLevel,
            data: [],
          });

          return newLevels;
        });
      }
    } catch (err) {
      console.error('Error fetching downline level:', err);
    } finally {
      setLoadingLevels((prev) => ({ ...prev, [levelKey]: false }));
    }
  };

  // ── handle clicking on a user in downline table ────────────────────────
  const handleUserClick = function (clickedUserId, clickedUserLevel, levelIndex) {
    // The clicked user's level is clickedUserLevel
    // We want to fetch THEIR downline (one level below them)
    // So we fetch with their userId and their level, then the API returns their children
    
    // Only fetch if the clicked user has potential downline (not a client themselves)
    if (clickedUserLevel > USER_LEVELS.CLIENT) {
      fetchDownlineLevel(clickedUserId, clickedUserLevel, levelIndex + 1);
    }
  };

  // ── helpers ──────────────────────────────────────────────────────────
  const handleBack = function () {
    navigate(-1);
  };

  const getLevelName = function (userLevel) {
    return LEVEL_NAMES[userLevel] || 'Unknown';
  };

  const isClickable = function (userLevel) {
    // Can click if the user has downline (i.e., not a client)
    return userLevel > USER_LEVELS.CLIENT;
  };

  // ── render downline table ────────────────────────────────────────────
  const renderDownlineTable = function (level) {
    const levelKey = `${level.levelIndex}-${level.userId}`;
    const isLoading = loadingLevels[levelKey];
    
    // The level.data contains users that are CHILDREN of level.userId
    // So if level.userId is a SUPER_MASTER (level 5), the data contains MASTERs (level 4)
    // We need to determine what level the children are
    const parentLevel = level.userLevel;
    const childLevel = parentLevel - 1;
    const childLevelName = getLevelName(childLevel);

    return (
      <div key={levelKey} className="mb-4">
        <div
          className="bg-white rounded"
          style={{
            border: '1px solid #d9d9d9',
            width: '100%',
          }}
        >
          {/* Table Header */}
          <div
            style={{
              padding: '12px 24px',
              borderBottom: '1px solid #f0f0f0',
              background: 'var(--primary)',
              borderRadius: '2px 2px 0 0',
            }}
          >
            <div
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: '#1a1a2e',
              }}
            >
              {childLevelName} Level - Downline of {level.userId}
            </div>
          </div>

          {/* Table Body */}
          <div style={{ padding: '16px' }}>
            {isLoading ? (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              </div>
            ) : level.data.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                No {childLevelName} users found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table
                  className="w-full"
                  style={{
                    borderCollapse: 'collapse',
                    fontSize: '13px',
                  }}
                >
                  <thead>
                    <tr style={{ background: 'var(--primary)' }}>
                      <th
                        style={{
                          border: '1px solid #e8e8e8',
                          padding: '8px 12px',
                          textAlign: 'left',
                          fontWeight: 600,
                          color: '#1a1a2e',
                        }}
                      >
                        User ID
                      </th>
                      <th
                        style={{
                          border: '1px solid #e8e8e8',
                          padding: '8px 12px',
                          textAlign: 'center',
                          fontWeight: 600,
                          color: '#1a1a2e',
                        }}
                      >
                        P&L
                      </th>
                      <th
                        style={{
                          border: '1px solid #e8e8e8',
                          padding: '8px 12px',
                          textAlign: 'center',
                          fontWeight: 600,
                          color: '#1a1a2e',
                        }}
                      >
                        Commission
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {level.data.map(function (userPnl, index) {
                      const pnlValue = Number(userPnl.pnl) || 0;
                      const commValue = Number(userPnl.comm) || 0;
                      
                      // The users in this table are at childLevel
                      // Check if they are clickable (i.e., they have downline)
                      const clickable = isClickable(childLevel);

                      return (
                        <tr key={index}>
                          <td
                            style={{
                              border: '1px solid #e8e8e8',
                              padding: '8px 12px',
                            }}
                          >
                            {clickable ? (
                              <span
                                onClick={function () {
                                  handleUserClick(
                                    userPnl.userId,
                                    childLevel,
                                    level.levelIndex
                                  );
                                }}
                                style={{
                                  color: '#1890ff',
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                }}
                              >
                                {userPnl.userId}
                              </span>
                            ) : (
                              <span>{userPnl.userId}</span>
                            )}
                          </td>
                          <td
                            style={{
                              border: '1px solid #e8e8e8',
                              padding: '8px 12px',
                              textAlign: 'center',
                              fontWeight: 600,
                              color: pnlValue >= 0 ? '#52c41a' : '#f5222d',
                            }}
                          >
                            {pnlValue.toFixed(2)}
                          </td>
                          <td
                            style={{
                              border: '1px solid #e8e8e8',
                              padding: '8px 12px',
                              textAlign: 'center',
                              fontWeight: 600,
                            }}
                          >
                            {commValue.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── render main bets table ───────────────────────────────────────────
  return (
    <div className="p-3" style={{ margin: '12px', width: 'calc(100% - 24px)' }}>
      {/* Downline Tables - only show if user has downline capability */}
      {loggedInUserLevel > USER_LEVELS.CLIENT &&
        downlineLevels.map(function (level) {
          return renderDownlineTable(level);
        })}

      {/* Main Bets Card */}
      <div
        className="bg-white rounded"
        style={{
          border: '1px solid #d9d9d9',
          width: '100%',
        }}
      >
        {/* Card Head */}
        <div
          className="flex items-center justify-between"
          style={{
            padding: '12px 24px',
            borderBottom: '1px solid #f0f0f0',
            background: 'var(--primary)',
            borderRadius: '2px 2px 0 0',
          }}
        >
          <div
            style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#1a1a2e',
            }}
          >
            Event Profit and Loss
          </div>

          <div>
            <button
              onClick={handleBack}
              style={{
                marginLeft: '10px',
                padding: '4px 15px',
                fontSize: '14px',
                height: '32px',
                border: '1px solid #d9d9d9',
                borderRadius: '2px',
                background: '#fff',
                color: 'rgba(0,0,0,0.85)',
                cursor: 'pointer',
                transition: 'all 0.3s',
              }}
              onMouseEnter={function (e) {
                e.currentTarget.style.background = '#f5f5f5';
              }}
              onMouseLeave={function (e) {
                e.currentTarget.style.background = '#fff';
              }}
            >
              Back
            </button>
          </div>
        </div>

        {/* Card Body */}
        <div style={{ padding: '24px' }}>
          {/* loading spinner */}
          {loading && (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            </div>
          )}

          {/* error banner */}
          {!loading && error && (
            <div className="text-center py-8 text-red-500 text-sm">{error}</div>
          )}

          {/* table (only when not loading) */}
          {!loading && !error && (
            <div className="overflow-x-auto">
              <table
                className="w-full"
                style={{
                  borderCollapse: 'collapse',
                  fontSize: '13px',
                }}
              >
                {/* thead */}
                <thead>
                  <tr style={{ background: 'var(--primary)' }}>
                    {[
                      'username',
                      'Date',
                      'Selection',
                      'Result',
                      'Back/Lay',
                      'Value',
                      'Volume',
                      'Stake',
                      'pnl',
                    ].map(function (header) {
                      return (
                        <th
                          key={header}
                          style={{
                            border: '1px solid #e8e8e8',
                            padding: '8px 12px',
                            textAlign: 'left',
                            fontWeight: 600,
                            color: '#1a1a2e',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {header}
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                {/* tbody */}
                <tbody>
                  {bets.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="text-center text-gray-400"
                        style={{ padding: '40px 0', border: '1px solid #e8e8e8' }}
                      >
                        <div className="flex flex-col items-center justify-center">
                          <svg
                            width="64"
                            height="41"
                            viewBox="0 0 64 41"
                            xmlns="http://www.w3.org/2000/svg"
                            className="mb-2"
                          >
                            <g transform="translate(0 1)" fill="none" fillRule="evenodd">
                              <ellipse fill="#f5f5f5" cx="32" cy="33" rx="32" ry="7" />
                              <g fillRule="nonzero" stroke="#d9d9d9">
                                <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z" />
                                <path
                                  d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z"
                                  fill="#fafafa"
                                />
                              </g>
                            </g>
                          </svg>
                          <div>No data</div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    bets.map(function (bet, index) {
                      const pnlValue = Number(bet.netPnl) || 0;
                      const rowBg = pnlValue < 0 ? '#fff1f0' : '#f6ffed';
                      const rowBorder = pnlValue < 0 ? '#ffccc7' : '#d4edda';

                      return (
                        <tr key={index} style={{ background: rowBg }}>
                          <td
                            style={{
                              border: '1px solid ' + rowBorder,
                              padding: '8px 12px',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {bet.username}{' '}
                            {bet.userId && (
                              <span style={{ color: '#666' }}>({bet.userId})</span>
                            )}
                          </td>

                          <td
                            style={{
                              border: '1px solid ' + rowBorder,
                              padding: '8px 12px',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {bet.time || '-'}
                          </td>

                          <td
                            style={{
                              border: '1px solid ' + rowBorder,
                              padding: '8px 12px',
                            }}
                          >
                            {bet.selectionName || bet.selection || '-'}
                          </td>

                          <td
                            style={{
                              border: '1px solid ' + rowBorder,
                              padding: '8px 12px',
                              textAlign: 'center',
                            }}
                          >
                            {bet.declared !== undefined && bet.declared !== null
                              ? bet.declared
                              : '-'}
                          </td>

                          <td
                            style={{
                              border: '1px solid ' + rowBorder,
                              padding: '8px 12px',
                              textAlign: 'center',
                            }}
                          >
                            <span
                              style={{
                                fontWeight: 600,
                                color:
                                  bet.mode === 'YES' || bet.back ? '#1a7a1a' : '#c10007',
                              }}
                            >
                              {bet.mode || (bet.back ? 'YES' : 'NO')}
                            </span>
                          </td>

                          <td
                            style={{
                              border: '1px solid ' + rowBorder,
                              padding: '8px 12px',
                              textAlign: 'center',
                            }}
                          >
                            {bet.rate !== undefined && bet.rate !== null ? bet.rate : '-'}
                          </td>

                          <td
                            style={{
                              border: '1px solid ' + rowBorder,
                              padding: '8px 12px',
                              textAlign: 'center',
                            }}
                          >
                            {bet.run !== undefined && bet.run !== null ? bet.run : '-'}
                          </td>

                          <td
                            style={{
                              border: '1px solid ' + rowBorder,
                              padding: '8px 12px',
                              textAlign: 'center',
                              fontWeight: 600,
                            }}
                          >
                            {bet.amount !== undefined && bet.amount !== null
                              ? bet.amount
                              : '-'}
                          </td>

                          <td
                            style={{
                              border: '1px solid ' + rowBorder,
                              padding: '8px 12px',
                              textAlign: 'center',
                              fontWeight: 700,
                              color: pnlValue < 0 ? '#c10007' : '#1a7a1a',
                            }}
                          >
                            {pnlValue}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompletedFancyBetsPage;